import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Search, TrendingUp, TrendingDown, Star } from 'lucide-react';
import { toast } from 'sonner';

function Sparkline({ data, up }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 30 - ((v - min) / range) * 30;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-24 h-8">
      <polyline points={points} fill="none" stroke={up ? '#0ECB81' : '#F6465D'} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function MoverCard({ coin, kind }) {
  const up = kind === 'gainer';
  return (
    <Link to={`/trade/${coin.id}`} data-testid={`mover-${kind}-${coin.symbol}`}
      className="flex-shrink-0 min-w-[220px] bg-[#181A20] border border-[#2B3139] hover:border-[#FCD535]/50 p-4 transition-colors">
      <div className="flex items-center gap-2 mb-3">
        <img src={coin.image} alt="" className="w-6 h-6" />
        <div>
          <div className="text-white text-sm font-semibold">{coin.symbol.toUpperCase()}</div>
          <div className="text-[#848E9C] text-xs">{coin.name}</div>
        </div>
        <div className="ml-auto">
          {up ? <TrendingUp className="w-4 h-4 text-[#0ECB81]" /> : <TrendingDown className="w-4 h-4 text-[#F6465D]" />}
        </div>
      </div>
      <div className="font-mono text-white text-lg">${coin.current_price?.toLocaleString(undefined, { maximumFractionDigits: coin.current_price < 1 ? 6 : 2 })}</div>
      <div className={`font-mono text-sm ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
        {up ? '+' : ''}{coin.price_change_percentage_24h?.toFixed(2)}%
      </div>
    </Link>
  );
}

export default function Markets() {
  const { user } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [watchIds, setWatchIds] = useState(new Set());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/markets');
        if (alive) { setMarkets(res.data.markets); setLoading(false); }
      } catch (_e) { if (alive) setLoading(false); }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  useEffect(() => {
    if (!user) { setWatchIds(new Set()); return; }
    api.get('/watchlist').then(r => setWatchIds(new Set(r.data.coin_ids))).catch(() => {});
  }, [user]);

  const toggleStar = async (e, coin_id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.info('Sign in to save coins to your watchlist'); return; }
    try {
      const r = await api.post('/watchlist/toggle', { coin_id });
      setWatchIds(new Set(r.data.coin_ids));
      toast.success(r.data.action === 'added' ? 'Added to watchlist' : 'Removed from watchlist');
    } catch (_e) { toast.error('Failed'); }
  };

  const filtered = markets.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.symbol.toLowerCase().includes(query.toLowerCase())
  );

  // Compute top gainers/losers (min market cap rank cap of 200 to avoid microcaps)
  const eligible = markets.filter(m => (m.market_cap_rank || 999) <= 200 && m.price_change_percentage_24h != null);
  const gainers = [...eligible].sort((a, b) => b.price_change_percentage_24h - a.price_change_percentage_24h).slice(0, 5);
  const losers = [...eligible].sort((a, b) => a.price_change_percentage_24h - b.price_change_percentage_24h).slice(0, 5);

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Markets</h1>
          <p className="text-[#848E9C] text-sm mt-1">Live prices from CoinGecko · refreshed every 60s</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5E6673]" />
          <Input placeholder="Search coin or symbol..." value={query} onChange={e=>setQuery(e.target.value)}
            className="pl-9 bg-[#181A20] border-[#2B3139] rounded-sm h-10 text-white focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
            data-testid="markets-search" />
        </div>
      </div>

      {/* Trending strips */}
      {gainers.length > 0 && (
        <div className="mb-8 space-y-4" data-testid="trending-section">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-[#0ECB81]" />
              <div className="text-xs uppercase tracking-widest text-[#EAECEF] font-semibold">Top Gainers · 24h</div>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="gainers-strip">
              {gainers.map(c => <MoverCard key={c.id} coin={c} kind="gainer" />)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-4 h-4 text-[#F6465D]" />
              <div className="text-xs uppercase tracking-widest text-[#EAECEF] font-semibold">Top Losers · 24h</div>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="losers-strip">
              {losers.map(c => <MoverCard key={c.id} coin={c} kind="loser" />)}
            </div>
          </div>
        </div>
      )}

      <div className="border border-[#2B3139] bg-[#181A20] overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-1">#</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-2 text-right">24h %</div>
          <div className="col-span-2 text-right">Market Cap</div>
          <div className="col-span-1 text-right">Chart</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">Loading markets...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No coins match your search.</div>
        ) : (
          filtered.map(c => {
            const up = c.price_change_percentage_24h >= 0;
            const starred = watchIds.has(c.id);
            return (
              <Link key={c.id} to={`/trade/${c.id}`} data-testid={`market-row-${c.symbol}`}
                className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-[#2B3139] transition-colors border-b border-[#2B3139]/50 items-center">
                <div className="col-span-1 text-[#848E9C] font-mono text-sm">{c.market_cap_rank}</div>
                <div className="col-span-3 flex items-center gap-3">
                  <img src={c.image} alt="" className="w-6 h-6" />
                  <div>
                    <div className="text-white text-sm font-medium">{c.name}</div>
                    <div className="text-[#848E9C] text-xs uppercase">{c.symbol}</div>
                  </div>
                </div>
                <div className="col-span-2 text-right font-mono text-white text-sm">
                  ${c.current_price?.toLocaleString(undefined, { maximumFractionDigits: c.current_price < 1 ? 6 : 2 })}
                </div>
                <div className={`col-span-2 text-right font-mono text-sm ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                  {up ? '+' : ''}{c.price_change_percentage_24h?.toFixed(2)}%
                </div>
                <div className="col-span-2 text-right font-mono text-[#B7BDC6] text-sm">
                  ${(c.market_cap / 1e9).toFixed(2)}B
                </div>
                <div className="col-span-1 flex justify-end">
                  <Sparkline data={c.sparkline_in_7d?.price} up={up} />
                </div>
                <div className="col-span-1 flex justify-end">
                  <button onClick={(e)=>toggleStar(e, c.id)} data-testid={`star-${c.symbol}`}
                    className={`p-1.5 rounded hover:bg-[#0B0E11] ${starred ? 'text-[#FCD535]' : 'text-[#5E6673] hover:text-[#FCD535]'}`}>
                    <Star className="w-4 h-4" fill={starred ? '#FCD535' : 'none'} />
                  </button>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
